/**
 * E024-S001-T006: DocSelector Component
 *
 * Dropdown-Komponente zur Auswahl eines Ziel-Dokuments.
 * - Filterbares Dropdown mit allen Docs im Workspace
 * - "Create New" Option wenn eingegebener Name nicht existiert
 * - Read-only Modus für Drag & Drop (zeigt aktuelles Doc)
 *
 * Verwendet in:
 * - CSV Import Preview
 * - Excel Import Preview
 * - (Future) Automation Sidebar
 */

import { Divider, Menu, MenuItem, type MenuProps } from '@affine/component';
import { DocsService } from '@affine/core/modules/doc';
import { DocDisplayMetaService } from '@affine/core/modules/doc-display-meta';
import { PageIcon, PlusIcon, SearchIcon } from '@blocksuite/icons/rc';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useLiveData, useService } from '@toeverything/infra';
import type { FC } from 'react';
import { useMemo, useState } from 'react';

import { useHiddenMailFolderDocIds } from '../../mail/mail-folder-visibility-filter';
import * as styles from '../automation-panel.css';

export interface DocInfo {
  id: string;
  title: string;
}

// Menu content options for proper positioning and scrolling
const menuContentOptions: MenuProps['contentOptions'] = {
  side: 'bottom',
  align: 'start',
  sideOffset: 4,
  style: {
    maxHeight: 'min(400px, calc(100vh - 200px))',
    overflowY: 'auto',
  },
};

export interface DocInfo {
  id: string;
  title: string;
}

interface DocSelectorProps {
  /** Currently selected doc ID or new doc name */
  value: string;
  /** Callback when selection changes */
  onChange: (value: string, isExistingDoc: boolean, docId?: string) => void;
  /** Optional predicate for restricting selectable docs */
  filterDoc?: (docId: string) => boolean;
  /** Whether free-text input may create a new document */
  canCreateNew?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Read-only mode (for drag & drop) */
  readOnly?: boolean;
  /** Label for read-only display */
  readOnlyLabel?: string;
}

/**
 * Helper component to display doc title reactively
 */
const DocTitle: FC<{ docId: string }> = ({ docId }) => {
  const docDisplayMetaService = useService(DocDisplayMetaService);
  const title = useLiveData(docDisplayMetaService.title$(docId));
  return <>{title || 'Untitled'}</>;
};

export const DocSelector: FC<DocSelectorProps> = ({
  value,
  onChange,
  filterDoc,
  canCreateNew = true,
  placeholder = 'Enter document name...',
  readOnly = false,
  readOnlyLabel,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const docsService = useService(DocsService);
  const docDisplayMetaService = useService(DocDisplayMetaService);
  const docRecords = useLiveData(docsService.list.docs$);
  const hiddenMailFolderDocIds = useHiddenMailFolderDocIds();

  // Build list of docs with titles
  const docs = useMemo(() => {
    if (!docRecords) return [];
    return docRecords
      .filter(record => !record.trash$.value)
      .filter(record => !hiddenMailFolderDocIds.has(record.id))
      .filter(record => filterDoc?.(record.id) ?? true)
      .map(record => ({
        id: record.id,
        title: docDisplayMetaService.title$(record.id).value || 'Untitled',
      }));
  }, [docRecords, docDisplayMetaService, filterDoc, hiddenMailFolderDocIds]);

  // Filter docs based on search query
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredDocs = useMemo(() => {
    if (!normalizedQuery) return docs;
    return docs.filter(doc =>
      doc.title.toLowerCase().includes(normalizedQuery)
    );
  }, [docs, normalizedQuery]);

  // Check if current value matches an existing doc
  const selectedDoc = useMemo(() => {
    return docs.find(doc => doc.id === value || doc.title === value);
  }, [docs, value]);

  // Check if search query exactly matches an existing doc title
  const exactMatch = useMemo(() => {
    if (!normalizedQuery) return null;
    return docs.find(doc => doc.title.toLowerCase() === normalizedQuery);
  }, [docs, normalizedQuery]);

  // Show "Create New" option if query doesn't exactly match any doc
  const showCreateNew = canCreateNew && normalizedQuery && !exactMatch;

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
  };

  const handleSearchBlur = () => {
    requestAnimationFrame(() => {
      const activeElement = document.activeElement;
      if (
        !(activeElement instanceof HTMLElement) ||
        activeElement.dataset.docSelectorSearch !== 'true'
      ) {
        setIsSearchFocused(false);
      }
    });
  };

  const pointerFocusGuardProps = isSearchFocused
    ? {
        onPointerMove: (event: { preventDefault: () => void }) =>
          event.preventDefault(),
        onPointerLeave: (event: { preventDefault: () => void }) =>
          event.preventDefault(),
      }
    : undefined;

  const handleSelectDoc = (doc: DocInfo) => {
    onChange(doc.title, true, doc.id);
    setSearchQuery('');
  };

  const handleCreateNew = () => {
    const newName = searchQuery.trim();
    if (newName) {
      onChange(newName, false);
      setSearchQuery('');
    }
  };

  // Display text for the button
  const displayText = readOnlyLabel
    ? readOnlyLabel
    : selectedDoc
      ? selectedDoc.title
      : value || placeholder;

  // Read-only mode: just show the label
  if (readOnly) {
    return (
      <div className={styles.fieldRow} style={{ cursor: 'default' }}>
        <PageIcon style={{ flexShrink: 0, opacity: 0.6 }} />
        <span className={styles.fieldLabel}>{displayText}</span>
      </div>
    );
  }

  const menuItems = (
    <>
      {/* Search Input */}
      <MenuItem
        onSelect={event => event.preventDefault()}
        className={styles.menuSearchItem}
        {...pointerFocusGuardProps}
      >
        <div
          className={styles.menuSearchContent}
          onClick={event => event.stopPropagation()}
          onKeyDown={event => event.stopPropagation()}
        >
          <SearchIcon className={styles.menuSearchIcon} />
          <input
            type="text"
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
            placeholder="Search or create new..."
            className={styles.menuSearchInput}
            data-doc-selector-search="true"
          />
        </div>
      </MenuItem>
      <Divider />

      {/* Create New Option */}
      {showCreateNew && (
        <>
          <MenuItem
            prefixIcon={<PlusIcon />}
            onClick={handleCreateNew}
            {...pointerFocusGuardProps}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              Create &quot;{searchQuery.trim()}&quot;
            </span>
          </MenuItem>
          <Divider />
        </>
      )}

      {/* Doc List */}
      {filteredDocs.length === 0 ? (
        <MenuItem disabled {...pointerFocusGuardProps}>
          No documents found
        </MenuItem>
      ) : (
        filteredDocs.slice(0, 20).map(doc => (
          <MenuItem
            key={doc.id}
            prefixIcon={<PageIcon />}
            selected={doc.id === selectedDoc?.id}
            onClick={() => handleSelectDoc(doc)}
            {...pointerFocusGuardProps}
          >
            <DocTitle docId={doc.id} />
          </MenuItem>
        ))
      )}

      {/* Show count if more docs available */}
      {filteredDocs.length > 20 && (
        <MenuItem disabled {...pointerFocusGuardProps}>
          ... and {filteredDocs.length - 20} more
        </MenuItem>
      )}
    </>
  );

  return (
    <Menu items={menuItems} contentOptions={menuContentOptions}>
      <button className={styles.fieldRow} style={{ cursor: 'pointer' }}>
        <PageIcon style={{ flexShrink: 0, opacity: 0.6 }} />
        <span className={styles.fieldLabel}>{displayText}</span>
        <span style={{ color: cssVarV2('text/tertiary') }}>▼</span>
      </button>
    </Menu>
  );
};
